class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.config = {
      reopenDays: parseInt(this.dataset.reopen, 10) || 1,
      sectionId: this.dataset.sectionId
    };
    this.elements = {
      opener: document.querySelector('.newsletter-modal__opener'),
      form: this.querySelector('form'),
      success: this.querySelector('[data-newsletter-success]'),
      error: this.querySelector('#ContactPopup-error'),
      code: this.querySelector('[data-newsletter-code]'),
      copy: this.querySelector('[data-newsletter-copy]'),
      copyStatus: this.querySelector('[data-newsletter-copy-status]'),
      apply: this.querySelector('[data-newsletter-apply]')
    };
    this.focusTimeout = null;
    this.closeTimeout = null;
    this.pendingKey = 'zenifyNewsletterPending';
    this.returnPath = this.getReturnPath();
    this.prepareLinks();
    this.attachEvents();
    this.restoreResult();
    this.setupDesignMode();
  }

  storage(type, action, key, value) {
    try { return window[type][action](key, value); } catch { return null; }
  }

  getReturnPath() {
    const url = new URL(window.location.href);
    ['customer_posted', 'contact_posted', 'form_type', 'utf8'].forEach(key => url.searchParams.delete(key));
    return url.pathname + url.search;
  }

  prepareLinks() {
    const currentPath = this.getReturnPath();
    const returnedElsewhere = this.offerReturnPath && new URL(this.offerReturnPath, window.location.origin).pathname !== window.location.pathname;
    this.returnPath = returnedElsewhere ? this.offerReturnPath : currentPath;
    if (this.elements.apply && this.elements.code) {
      this.elements.apply.href = '/discount/' + encodeURIComponent(this.elements.code.value.trim()) +
        '?redirect=' + encodeURIComponent(this.returnPath);
    }
  }

  getPendingSubmission() {
    try {
      const pending = JSON.parse(this.storage('sessionStorage', 'getItem', this.pendingKey));
      if (!pending || pending.form !== 'ContactPopup' || !Number.isFinite(pending.time)) return null;
      const age = Date.now() - pending.time;
      if (age < 0 || age >= 1800000 || !pending.path?.startsWith('/')) return null;
      const target = new URL(pending.path, window.location.origin);
      if (target.origin !== window.location.origin) return null;
      return { ...pending, path: target.pathname + target.search };
    } catch { return null; }
  }

  restoreResult() {
    const pending = this.getPendingSubmission();
    const isPopupReturn = pending || window.location.hash === '#ContactPopup';
    if (isPopupReturn && (this.elements.success || this.elements.error)) {
      document.querySelectorAll('form[action*="/contact"] [autofocus]').forEach(element => {
        if (!this.contains(element)) element.removeAttribute('autofocus');
      });
      this.restorePagePosition(pending);
    }
    // Only Shopify's server-rendered form result confirms subscription.
    if (this.elements.success) {
      this.offerReturnPath = pending?.path || this.getReturnPath();
      this.prepareLinks();
      this.classList.add('newsletter-modal--success');
      this.querySelectorAll('[data-newsletter-intro]').forEach(element => element.remove());
      this.storage('localStorage', 'setItem', 'newsletterSubscribed', 'true');
      this.storage('sessionStorage', 'removeItem', this.pendingKey);
      this.toggleOpener(this.isExpired('newsletterOpenerClosed'));
      if (isPopupReturn) this.show(this.elements.opener?.querySelector('button'));
      return;
    }
    if (this.elements.error && isPopupReturn) {
      this.storage('sessionStorage', 'removeItem', this.pendingKey);
      this.toggleOpener(true);
      this.show(this.elements.opener?.querySelector('button'));
      return;
    }
    this.toggleOpener(this.isExpired('newsletterOpenerClosed'));
  }

  isExpired(key) {
    const timestamp = Number(this.storage('localStorage', 'getItem', key));
    return !timestamp || Date.now() > timestamp;
  }

  restorePagePosition(pending) {
    if (!pending || new URL(pending.path, window.location.origin).pathname !== window.location.pathname) return;
    if (!Number.isFinite(pending.scrollY) || pending.scrollY < 0) return;
    const position = {
      left: Number.isFinite(pending.scrollX) && pending.scrollX >= 0 ? pending.scrollX : 0,
      top: pending.scrollY,
      behavior: 'instant'
    };
    window.scrollTo(position);
    if (document.readyState !== 'complete') {
      this.pageShowRestore = () => {
        if (this.hasAttribute('open')) window.scrollTo(position);
      };
      window.addEventListener('pageshow', this.pageShowRestore, { once: true });
    }
  }

  setExpiration(key) {
    this.storage('localStorage', 'setItem', key, String(Date.now() + this.config.reopenDays * 86400000));
  }

  toggleOpener(show) {
    if (this.elements.opener) this.elements.opener.style.display = show ? '' : 'none';
  }

  clearTimers() {
    ['focusTimeout', 'closeTimeout'].forEach(key => {
      clearTimeout(this[key]);
      this[key] = null;
    });
  }

  attachEvents() {
    this.elements.apply?.addEventListener('click', () => this.prepareLinks());
    this.elements.form?.addEventListener('submit', () => {
      this.prepareLinks();
      // Keep the native form and captcha flow; do not mark success on submit.
      const pending = JSON.stringify({
        form: 'ContactPopup', path: this.getReturnPath(), time: Date.now(),
        scrollX: window.scrollX, scrollY: window.scrollY
      });
      this.storage('sessionStorage', 'setItem', this.pendingKey, pending);
      if (this.storage('sessionStorage', 'getItem', this.pendingKey) === pending) {
        const action = new URL(this.elements.form.action, window.location.origin);
        if (action.origin === window.location.origin) {
          action.hash = '';
          this.elements.form.action = action.pathname + action.search;
        }
      }
    });
    this.elements.opener?.querySelector('.newsletter-modal__close')?.addEventListener('click', event => {
      event.stopPropagation();
      this.toggleOpener(false);
      this.setExpiration('newsletterOpenerClosed');
    });
    this.elements.copy?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(this.elements.code.value);
        this.elements.copyStatus.textContent = this.elements.copy.dataset.copied;
      } catch {
        this.elements.code.focus({ preventScroll: true });
        this.elements.code.select();
        this.elements.copyStatus.textContent = this.elements.copy.dataset.copyFallback;
      }
    });
  }

  show(opener) {
    this.clearTimers();
    this.openedBy = opener || document.activeElement;
    this.removeAttribute('closing');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.focusTimeout = setTimeout(() => {
      if (!this.isConnected || !this.hasAttribute('open') || this.hasAttribute('closing')) return;
      const position = { left: window.scrollX, top: window.scrollY, behavior: 'instant' };
      trapFocus(this, this.elements.success || this.elements.error || this.querySelector('input[type="email"], button'));
      window.scrollTo(position);
    }, 300);
  }

  hide() {
    this.clearTimers();
    window.removeEventListener('pageshow', this.pageShowRestore);
    this.setAttribute('closing', 'true');
    this.closeTimeout = setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.removeAttribute('closing');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      removeTrapFocus();
      if (this.openedBy?.isConnected) this.openedBy.focus({ preventScroll: true });
    }, 300);
    this.setExpiration('newsletterPopupClosed');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearTimers();
    this.designController?.abort();
    window.removeEventListener('pageshow', this.pageShowRestore);
    if (this.hasAttribute('open')) {
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus();
    }
  }

  setupDesignMode() {
    if (!window.Shopify?.designMode) return;
    this.designController = new AbortController();
    const options = { signal: this.designController.signal };
    document.addEventListener('shopify:section:select', event => {
      if (event.detail.sectionId === this.config.sectionId) this.show();
    }, options);
    document.addEventListener('shopify:section:deselect', event => {
      if (event.detail.sectionId === this.config.sectionId) this.hide();
    }, options);
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
